/* global axios */
import ApiClient from '../ApiClient';

class BillingAPI extends ApiClient {
  constructor() {
    super('omni/billing', { accountScoped: true });
  }

  // GET /api/v1/accounts/:account_id/omni/billing
  get() {
    return axios.get(this.url);
  }

  // GET /api/v1/accounts/:account_id/omni/billing/plans
  getPlans() {
    return axios.get(`${this.url}/plans`);
  }

  // GET /api/v1/accounts/:account_id/omni/billing/payments
  getPayments(params = {}) {
    return axios.get(`${this.url}/payments`, { params });
  }

  // GET /api/v1/accounts/:account_id/omni/billing/upcoming_invoice
  getUpcomingInvoice() {
    return axios.get(`${this.url}/upcoming_invoice`);
  }

  // POST /api/v1/accounts/:account_id/omni/billing/checkout
  // Combined checkout for plan + addons
  // addons: Array of { id: number, quantity: number }
  createCheckout(
    planId,
    billingCycle,
    addons = [],
    successUrl = null,
    cancelUrl = null
  ) {
    return axios.post(`${this.url}/checkout`, {
      plan_id: planId,
      billing_cycle: billingCycle,
      addons: addons,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
  }

  // POST /api/v1/accounts/:account_id/omni/billing/update_subscription
  // Update existing subscription (add/remove addons, change plan)
  updateSubscription(planId = null, addons = null, billingCycle = null) {
    return axios.post(`${this.url}/update_subscription`, {
      plan_id: planId,
      addons: addons,
      billing_cycle: billingCycle,
    });
  }

  // POST /api/v1/accounts/:account_id/omni/billing/upgrade_plan
  // Upgrade to a new plan while preserving existing addons
  // This modifies the existing Stripe subscription instead of creating a new one
  upgradePlan(planId, billingCycle = null) {
    return axios.post(`${this.url}/upgrade_plan`, {
      plan_id: planId,
      billing_cycle: billingCycle,
    });
  }

  // POST /api/v1/accounts/:account_id/omni/billing/portal
  createPortal(returnUrl = null) {
    return axios.post(`${this.url}/portal`, {
      return_url: returnUrl,
    });
  }

  // POST /api/v1/accounts/:account_id/omni/billing/cancel
  cancelSubscription(atPeriodEnd = true) {
    return axios.post(`${this.url}/cancel`, {
      at_period_end: atPeriodEnd,
    });
  }

  // POST /api/v1/accounts/:account_id/omni/billing/resume
  resumeSubscription() {
    return axios.post(`${this.url}/resume`);
  }

  // GET /api/v1/accounts/:account_id/omni/billing/exceeding_resources
  // Get summary of resources exceeding plan limits
  getExceedingResources() {
    return axios.get(`${this.url}/exceeding_resources`);
  }

  // POST /api/v1/accounts/:account_id/omni/billing/enforce_limits
  // Enforce plan limits with specific selections
  // selections: { bots: [ids], inboxes: [ids], users: [ids] }
  enforceLimits(selections) {
    return axios.post(`${this.url}/enforce_limits`, selections);
  }

  // POST /api/v1/accounts/:account_id/omni/billing/auto_enforce_limits
  // Auto-enforce by disabling oldest exceeding resources
  autoEnforceLimits() {
    return axios.post(`${this.url}/auto_enforce_limits`);
  }

  // POST /api/v1/accounts/:account_id/omni/billing/reenable_resources
  // Re-enable all disabled resources
  reenableResources() {
    return axios.post(`${this.url}/reenable_resources`);
  }

  // GET /api/v1/accounts/:account_id/omni/billing/reenableable_resources
  // Get summary of disabled resources that can be re-enabled
  getReenableableResources() {
    return axios.get(`${this.url}/reenableable_resources`);
  }

  // POST /api/v1/accounts/:account_id/omni/billing/reenable_selected
  // Re-enable specific resources by IDs
  // selections: { bots: [ids], inboxes: [ids], users: [ids] }
  reenableSelected(selections) {
    return axios.post(`${this.url}/reenable_selected`, selections);
  }

  // GET /api/v1/accounts/:account_id/omni/billing/plan_status
  // Get plan status for dashboard check
  getPlanStatus() {
    return axios.get(`${this.url}/plan_status`);
  }
}

export default new BillingAPI();

